/**
 * MetadataForm Component
 *
 * Purpose: Reusable form component that dynamically generates form inputs based on metadata
 * Architecture: Fetches view configuration, renders appropriate inputs, validates and serializes data
 * Dependencies: Material-UI, useMetadataForm hook, field renderers
 * Author: Development Team
 * Created: 2025-01-31
 */

import React, { useMemo, useCallback, useImperativeHandle } from 'react';
import { Box, TextField, Alert, Divider, Typography, CircularProgress } from '@mui/material';
import { useMetadataForm } from './useMetadataForm';
import { getFieldRenderer } from './FieldRenderers';
import type { MetadataFormProps } from './types';
import { PickerFilters, PickerScope } from '../../context/PickerScopeContext';
import { resolvePickerPermission } from '../../utils/pickerPermission';
import { fieldGridSx, fieldSpanAllSx, spansAllColumns } from '../form-layout/fieldGrid';

/**
 * Imperative handle exposed via forwardRef so parent buttons rendered outside the
 * form DOM tree (e.g. in MUI DialogActions portals) can trigger submission without
 * relying on form.requestSubmit(), which does not fire React's delegated onSubmit
 * listener for Portal-mounted forms in React 17+.
 */
export interface MetadataFormHandle {
  submit: () => Promise<void>;
}

/**
 * MetadataForm Component
 *
 * Purpose: Dynamically renders form based on metadata view configuration
 * Logic: Fetches field definitions, renders appropriate inputs, handles validation and submission
 * Input: MetadataFormProps (see types.ts)
 * Output: Rendered form with fixed and dynamic fields
 * Exceptions: Displays error alert if view config fails to load
 */
export const MetadataForm = React.forwardRef<MetadataFormHandle, MetadataFormProps>(
  (
    {
      modelId,
      tenantId,
      mode,
      viewConfig: providedViewConfig,
      initialData,
      fixedFields = [],
      onSubmit,
      onValidationError,
      onChange,
      entityId,
      excludeFields = [],
      customRenderers = {},
      showSectionDivider = true,
      sectionTitle = 'Additional Fields',
      columnLayout = 'double',
      loading: externalLoading,
      readOnly = false,
      pickerPermission,
    },
    ref
  ) => {
    // ORCH-366: pickers are scoped by the model and mode unless the caller says otherwise
    const pickerScope =
      pickerPermission === undefined
        ? resolvePickerPermission(modelId, readOnly ? 'view' : mode)
        : pickerPermission;
    const {
      viewConfig,
      loading,
      error,
      formData,
      errors,
      touched,
      setValue,
      setMetadataValue,
      setTouched,
      validateForm,
      pickerLocked,
    } = useMetadataForm(modelId, tenantId, initialData, onChange, providedViewConfig, fixedFields, {
      pickerPermission: pickerScope,
      // ORCH-400: an edit form names its record so stored out-of-scope values are locked
      recordId: mode === 'edit' ? (entityId ?? initialData?.id ?? null) : null,
      readOnly,
    });

    const gridColumns = columnLayout === 'double' ? 2 : 1;

    // Filter out excluded fields
    const visibleFields = useMemo(() => {
      if (!viewConfig) return [];
      return viewConfig.fields.filter((field) => !excludeFields.includes(field.fielddef_id));
    }, [viewConfig, excludeFields]);

    const doSubmit = useCallback(async () => {
      const { isValid, errors: validationErrors } = validateForm();
      if (!isValid) {
        onValidationError?.(
          Object.entries(validationErrors).map(([fieldId, message]) => ({
            fieldId,
            message,
            type: 'required' as const,
          }))
        );
        return;
      }
      if (onSubmit) {
        await onSubmit(formData);
      }
    }, [validateForm, onValidationError, onSubmit, formData]);

    useImperativeHandle(ref, () => ({ submit: doSubmit }), [doSubmit]);

    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault();
      await doSubmit();
    };

    // Loading state
    if (loading || externalLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    // Error state
    if (error) {
      return <Alert severity="error">Failed to load form configuration: {error}</Alert>;
    }

    return (
      <PickerScope permission={pickerScope}>
        {/* ORCH-400: pickers narrow by the form's own values; locked chips keep no delete */}
        <PickerFilters
          modelId={modelId}
          tenantId={tenantId}
          values={formData.metadata}
          locked={pickerLocked}
        >
          <Box component="form" onSubmit={handleSubmit} noValidate>
            {/* Fixed fields section */}
            {fixedFields.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Box sx={fieldGridSx(gridColumns)}>
                  {fixedFields.map((fixedField) => (
                    <Box
                      sx={fixedField.fullWidth ? fieldSpanAllSx : undefined}
                      key={fixedField.fieldId}
                    >
                      {fixedField.render ? (
                        fixedField.render(
                          formData[fixedField.fieldId],
                          (value) => setValue(fixedField.fieldId, value),
                          readOnly || fixedField.readOnly || false,
                          touched[fixedField.fieldId] && !!errors[fixedField.fieldId],
                          touched[fixedField.fieldId] ? errors[fixedField.fieldId] : undefined
                        )
                      ) : (
                        <TextField
                          label={fixedField.label}
                          value={formData[fixedField.fieldId] || ''}
                          onChange={(e) => {
                            setValue(fixedField.fieldId, e.target.value);
                            setTouched(fixedField.fieldId);
                          }}
                          onBlur={() => setTouched(fixedField.fieldId)}
                          required={fixedField.required}
                          error={touched[fixedField.fieldId] && !!errors[fixedField.fieldId]}
                          helperText={touched[fixedField.fieldId] && errors[fixedField.fieldId]}
                          fullWidth
                          margin="none"
                          InputProps={{
                            readOnly: readOnly || fixedField.readOnly,
                          }}
                        />
                      )}
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {/* Divider between fixed and dynamic fields */}
            {showSectionDivider && fixedFields.length > 0 && visibleFields.length > 0 && (
              <Divider sx={{ my: 3 }} />
            )}

            {/* Dynamic fields section */}
            {visibleFields.length > 0 && (
              <Box>
                {sectionTitle && (
                  <Typography variant="h6" gutterBottom>
                    {sectionTitle}
                  </Typography>
                )}

                <Box sx={fieldGridSx(gridColumns)}>
                  {visibleFields.map((field) => {
                    // Get custom renderer or default renderer for datatype
                    const FieldRenderer =
                      customRenderers[field.fielddef_id] ||
                      getFieldRenderer(field.field_definition.datatype);

                    // Debug: Log field rendering details (disabled in production/tests)
                    // console.log('[MetadataForm] Rendering field:', {
                    //   fielddef_id: field.fielddef_id,
                    //   datatype: field.field_definition.datatype,
                    //   value: formData.metadata[field.fielddef_id],
                    //   hasValue: !!formData.metadata[field.fielddef_id],
                    //   rendererName: FieldRenderer.name
                    // });

                    return (
                      <Box
                        sx={spansAllColumns(field.field_definition) ? fieldSpanAllSx : undefined}
                        key={field.field_id}
                      >
                        <FieldRenderer
                          field={field}
                          value={formData.metadata[field.fielddef_id]}
                          onChange={(value) => {
                            setMetadataValue(field.fielddef_id, value);
                            setTouched(field.fielddef_id);
                          }}
                          error={errors[field.fielddef_id]}
                          touched={touched[field.fielddef_id]}
                          readOnly={readOnly}
                          entityId={entityId}
                        />
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            )}
          </Box>
        </PickerFilters>
      </PickerScope>
    );
  }
);

MetadataForm.displayName = 'MetadataForm';
